"use client";

import { CodeSnippet2 } from "@shared/components/ui/code-snippet2";
import { Tabs2, TabsList2, TabsTrigger2 } from "@shared/components/ui/tabs2";
import type { ShikiLang } from "@shared/lib/shiki";
import type { OnboardingProviderParam } from "@/lib/search-params/nuqs.onboarding";
import { cn } from "@/lib/utils";
import csharpOpenAiSnippet from "./snippets/csharp.openai.txt";
import curlOpenAiSnippet from "./snippets/curl.openai.txt";
import goOpenAiSnippet from "./snippets/go.openai.txt";
import javaOpenAiSnippet from "./snippets/java.openai.txt";
import nodeAnthropicSnippet from "./snippets/node.anthropic.txt";
import nodeOpenAiSnippet from "./snippets/node.openai.txt";
import pythonAnthropicSnippet from "./snippets/python.anthropic.txt";
import pythonOpenAiSnippet from "./snippets/python.openai.txt";

type Provider = "openai" | "anthropic" | null;

export const INTEGRATION_CODE_KEY_PLACEHOLDER = "YOUR_API_KEY";

const BASE_URL = "https://api.radium.cloud/v1";
const BASE_URL_ANTHROPIC = "https://api.radium.cloud";

/**
 * `providers`: languages with a dedicated SDK for this provider set.
 * Omit to show for all providers. curl is always shown.
 */
const LANG_TABS = [
  {
    id: "python",
    label: "Python",
    lang: "python" satisfies ShikiLang,
    providers: undefined,
  },
  {
    id: "typescript",
    label: "TypeScript",
    lang: "typescript" satisfies ShikiLang,
    providers: undefined,
  },
  {
    id: "curl",
    label: "curl",
    lang: "bash" satisfies ShikiLang,
    providers: undefined,
    alwaysShow: true,
  },
  {
    id: "go",
    label: "Go",
    lang: "go" satisfies ShikiLang,
    providers: [null, "openai"] as Provider[],
  },
  {
    id: "java",
    label: "Java",
    lang: "java" satisfies ShikiLang,
    providers: [null, "openai"] as Provider[],
  },
  {
    id: "csharp",
    label: "C#",
    lang: "csharp" satisfies ShikiLang,
    providers: [null, "openai"] as Provider[],
  },
] as const;

type LangId = (typeof LANG_TABS)[number]["id"];

const visibleTabs = (provider: Provider) =>
  LANG_TABS.filter((t) => ("alwaysShow" in t && t.alwaysShow) || !t.providers || t.providers.includes(provider));

const SNIPPETS: Record<LangId, Partial<Record<Exclude<Provider, null> | "default", string>>> = {
  python: {
    default: pythonOpenAiSnippet,
    anthropic: pythonAnthropicSnippet,
  },
  typescript: {
    default: nodeOpenAiSnippet,
    anthropic: nodeAnthropicSnippet,
  },
  curl: {
    default: curlOpenAiSnippet,
  },
  go: {
    default: goOpenAiSnippet,
  },
  java: {
    default: javaOpenAiSnippet,
  },
  csharp: {
    default: csharpOpenAiSnippet,
  },
};

const resolveSnippet = (id: LangId, provider: Provider): string =>
  (provider && SNIPPETS[id][provider]) ?? SNIPPETS[id].default ?? "";

const SNIPPET_VARIANTS: Array<{
  id: OnboardingProviderParam;
  label: string;
}> = [
  { id: "openai", label: "OpenAI SDK" },
  { id: "anthropic", label: "Anthropic SDK" },
];

/** Maps UI `provider` to the snippet resolver input (`anthropic` vs OpenAI-compatible). */
const snippetProviderFromParam = (provider: OnboardingProviderParam | null): Provider =>
  provider === "anthropic" ? "anthropic" : null;

export type IntegrationCodeSnippetsProps = {
  provider: OnboardingProviderParam | null;
  onProviderChange: (provider: OnboardingProviderParam) => void;
  /** Substituted into snippets. Defaults to YOUR_API_KEY. */
  apiKeyForSnippets?: string;
  copyDisabled?: boolean;
  copyDisabledMessage?: string;
  codeRegionClassName?: string;
  codeRegionBackgroundClassName?: string;
  className?: string;
};

/**
 * OpenAI / Anthropic SDK tabs + language-tabbed code snippets.
 * Shared by onboarding first-call and the dashboard integration sheet.
 */
export const IntegrationCodeSnippets = ({
  provider,
  onProviderChange,
  apiKeyForSnippets = INTEGRATION_CODE_KEY_PLACEHOLDER,
  copyDisabled = false,
  copyDisabledMessage,
  codeRegionClassName,
  codeRegionBackgroundClassName,
  className,
}: IntegrationCodeSnippetsProps) => {
  const snippetProvider = snippetProviderFromParam(provider);
  const activeProvider = provider ?? "openai";

  const getSnippet = (id: LangId) =>
    resolveSnippet(id, snippetProvider)
      .replaceAll("__API_KEY__", apiKeyForSnippets)
      .replaceAll("__BASE_URL_ANTHROPIC__", BASE_URL_ANTHROPIC)
      .replaceAll("__BASE_URL__", BASE_URL)
      .replaceAll(INTEGRATION_CODE_KEY_PLACEHOLDER, apiKeyForSnippets);

  const snippetTabs = visibleTabs(snippetProvider).map((tab) => ({
    id: tab.id,
    label: tab.label,
    lang: tab.lang,
    code: getSnippet(tab.id),
  }));

  return (
    <div className={cn(className)}>
      <Tabs2
        className="min-h-0 flex-1"
        value={activeProvider}
        onValueChange={(value) => {
          if (value === "openai" || value === "anthropic") {
            onProviderChange(value);
          }
        }}
      >
        <TabsList2 variant="primary" aria-label="Choose your SDK">
          {SNIPPET_VARIANTS.map((p) => (
            <TabsTrigger2 key={p.id} value={p.id} variant="primary" size="lg">
              {p.label}
            </TabsTrigger2>
          ))}
        </TabsList2>

        <CodeSnippet2
          key={activeProvider}
          tabs={snippetTabs}
          copyDisabled={copyDisabled}
          copyDisabledMessage={copyDisabledMessage}
          codeRegionClassName={codeRegionClassName}
          codeRegionBackgroundClassName={codeRegionBackgroundClassName}
          className="min-h-0 flex-1 rounded-b-[6px] bg-gray-50 pt-1"
        />
      </Tabs2>
    </div>
  );
};
